import { ClassKey } from "@const/class.constants";
import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { DefaultDashboardDTO, GroupDashboardFolder } from "@dto/dashboard.dto";
import { MenuItem, MenuResponseDTO } from "@dto/menuResponse.dto";
import { AxiosError } from "axios";
import { isEqual, pick } from "lodash-es";
import { Component, ReactNode } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import { BasePage } from "src/content/basePage/BasePage";
import { ErrorPage } from "src/content/errorBoundary/ErrorPage";
import { AllType, RoutePath, isDevicesTree } from "src/data/Routes";
import { menuItemsRight } from "src/data/menu-items";
import { MenuNode } from "src/model/MenuNode";
import { Log } from "src/service/log.service";
import { MenuService } from "src/service/menu.service";
import { GLOB } from "src/util/Glob";
import { getApi } from "src/util/apiCalls";
import { WithRouter, withRouter } from "src/util/router";
import { findNavDomain } from "./domain";
import { commitSelection, NavContext, NavResult } from "./domain/NavDomain";
import { findLanGroupType, isLanGroupType } from "./domain/lan.domain";
import { resolveDeviceTreeItem, resolveStorageRoot } from "./domain/storage.domain";
import { findLastTopologyNode } from "src/boxParts/topology/topologyPrefs";
import { resolveToolsTree } from "./domain/tools.domain";
import { EmptyPage } from "./EmptyPage";
import { getPathUrl } from "./pathUrl";

export class NavRouteProps {
  clsName: string = null;
  allType?: AllType = null;
  groupName?: string = null;
  typeOrDevice?: string = null;
  itemName: string = null;
  search?: string = null;
}

const NAV_ROUTE_PROPS = Object.keys(new NavRouteProps());

interface NavState {
  navItem: MenuNode;
  loading: boolean;
  search: string;
}

export function itemsToNodes(items: MenuItem[], group?: string): MenuNode[] {
  if (!items) return;
  return items.map((mi) => ({ ...MenuNode.menuItemToNode(mi, group), children: itemsToNodes(mi.children, group) }));
}

function equals(a: MenuItem, b: MenuNode) {
  return a.url && a.url === b.url || b.group_id && (a as GroupDashboardFolder).group_id === b.group_id;
}

export function mergeTree(current: MenuNode[], incoming: MenuItem[], group?: string, selectedClass = GLOB.selectedClass) {
  if (!current || !incoming) return false;
  let updated = false;
  for (const inc of incoming) {
    let found: MenuNode;
    for (let i = 0; i < current.length; i++) {
      const cur = current[i];
      if (equals(inc, cur)) {
        found = cur;
        if (cur.children) {
          if (cur.subsystem === "group" && inc.children) {
            cur.children = itemsToNodes(inc.children, group);
            updated = true;
            current[i] = { ...cur };
          } else {
            const result = mergeTree(cur.children, inc.children, group);
            if (result) {
              updated = true;
              current[i] = { ...cur };
            }
          }
        } else if (inc.children) {
          cur.children = itemsToNodes(inc.children, group);
          if (selectedClass === RoutePath.CUSTOM_GROUPS)
            cur.children.sort(MenuService.getFolderSorter(true));
          else if (selectedClass === RoutePath.DASHBOARD)
            cur.children.sort(MenuService.getFolderSorter(false));
          current[i] = { ...cur };
          updated = true;
        }
        break;
      }
    }
    if (!found) {
      current.push(itemsToNodes([inc], group)[0]);
      updated = true;
    }
  }
  return updated;
}

class NavigateRoute extends Component<WithRouter & NavRouteProps, NavState> {

  private aborter: AbortController;

  constructor(props: WithRouter & NavRouteProps) {
    super(props);
    this.state = { loading: true, navItem: null, search: null };
  }

  componentDidUpdate(prevProps: Readonly<WithRouter & NavRouteProps>, prevState: Readonly<NavState>, snapshot?): void {
    if (!isEqual(pick(prevProps, NAV_ROUTE_PROPS), pick(this.props, NAV_ROUTE_PROPS)) || prevState.search !== this.state.search) {
      this.navigate();
    }
  }
  componentWillUnmount(): void {
    this.aborter?.abort();
  }

  componentDidMount(): void {
    this.navigate();
  }

  private navigate() {
    GLOB.navAborter?.abort();
    const aborter = this.aborter = GLOB.navAborter = new AbortController();
    this.setState({ loading: true });

    GLOB.xormonReady.promise.then(() => {
      void GLOB.timeoutPromise(async () => {
        if (aborter.signal.aborted) return;
        const result = await this.getItem(aborter.signal);
        if (aborter.signal.aborted) return;
        if (!result) {
          this.setState({ loading: false });
          return;
        }
        setTimeout(() => {
          if (aborter.signal.aborted) return;
          if (result.tree) {
            GLOB.treeLoaded.reset();
            useAppGlobStore.setState({ leftMenu: result.tree });
          }
          const loc = this.props.location;
          if (result.nav && (loc.pathname + loc.search + loc.hash) !== result.nav.to) {
            this.props.navigate(result.nav.to, result.nav.options);
            return;
          }
          if (result.item)
            this.setState({ navItem: result.item });
          else if (result.tree || useAppGlobStore.getState().leftMenu?.children) {
            if ('/' + this.props.clsName !== GLOB.selectedClass) {
              Log.error('No page for route ' + this.props.location.pathname);
            }
            this.setState({ navItem: { key: 'empty', component: <EmptyPage /> } });
          }
          else {
            const defaultPath = GLOB.getDefaultPath();
            if (this.props.location.pathname === defaultPath) {
              this.setState({ navItem: { key: 'noItem', component: <ErrorPage detail={'No page for route ' + defaultPath} /> } });
            } else {
              Log.error('No menu found for specified URL', this.props);
              this.props.navigate(defaultPath);
              return;
            }
          }
          this.setState({ loading: false });
        });
      });

    }, console.debug);

  }

  private async getItem(abortSignal: AbortSignal): Promise<NavResult> {
    const result = new NavResult();
    const { clsName, allType, groupName, typeOrDevice, itemName } = this.props;
    const clsPath = '/' + clsName as RoutePath;
    const typeOrDevicePath = typeOrDevice ? '/' + typeOrDevice : null;
    const itemPath = '/' + itemName;
    const itemSearch = itemPath + this.props.location.search;
    const itemHash = this.props.location.hash;
    const ctx: NavContext = {
      clsPath, allType, groupName, typeOrDevice, typeOrDevicePath, itemName, itemSearch, itemHash,
      location: this.props.location,
    };

    const normalizedNav = findNavDomain(clsPath)?.normalize?.(ctx);
    if (normalizedNav) {
      result.nav = normalizedNav;
      return result;
    }

    if (!menuItemsRight) return result;
    let cls = menuItemsRight.find((mn) => mn.path === clsPath);
    if (!cls) {
      if (clsPath === GLOB.selectedClass &&
        (allType && allType === GLOB.selectedAllType || !allType && typeOrDevicePath === GLOB.selectedType)) {
        cls = useAppGlobStore.getState().leftMenu;
      }
      else {
        cls = MenuService.getActiveMenu().find((mn) => mn.path === clsPath);
      }
    }
    if (!cls) {
      return result;
    }
    const key = cls.keyClass || cls.key as string;
    if (!useAppGlobStore.getState().topMenuSelectedKeys.some(s => s === key)) {
      setTimeout(() => {
        useAppGlobStore.setState({ topMenuSelectedKeys: [key] });
      });
    }
    const hwTypePath = allType && typeOrDevice ? typeOrDevicePath + '/' : null;
    let nodes: MenuNode[];
    let groupPath = '';
    let group: string;
    let groupNode: MenuNode;
    if (key === ClassKey.STORAGE && (GLOB.selectedAllType || allType)) {
      const storageRoot = resolveStorageRoot(cls, ctx);
      if (storageRoot.nav) {
        result.nav = storageRoot.nav;
        return result;
      }
      ({ cls, nodes, group, groupPath, groupNode } = storageRoot);
    }

    if (!nodes) nodes = cls.children;
    // Use exact hwType matching only.
    // Substring matching caused collisions (e.g. "veeambr" matched "veeam").
    const matchesType = (mn: MenuNode) => {
      if (mn.path === typeOrDevicePath) return true;
      if (Array.isArray(mn.hwType)) return mn.hwType.includes(typeOrDevice);
      return mn.hwType === typeOrDevice;
    };
    // fabric can have 2 hw_types under it
    const findTypeDeep = (mns: MenuNode[]): MenuNode => {
      if (!mns) return;
      const direct = mns.find(matchesType);
      if (direct) return direct;
      for (const mn of mns) {
        const found = findTypeDeep(mn.children);
        if (found) return found;
      }
    };
    let typ = isDevicesTree(allType) ?
      typeOrDevice ? nodes?.find(mn => mn.title === typeOrDevice) : cls
      : typeOrDevice && !cls.hwType ? findTypeDeep(nodes)
        : cls;
    if (!typ && groupNode) typ = groupNode;
    if (!typ && isLanGroupType(typeOrDevice)) {
      typ = findLanGroupType(nodes, typeOrDevice, itemSearch);
    }
    const deviceTree = resolveDeviceTreeItem(ctx, cls, typ, nodes, group, groupPath);
    if (deviceTree) {
      typ = deviceTree.typ;
      if (deviceTree.result) return deviceTree.result;
    }
    const root = (allType || group) ? cls : typ;
    if (!typ && GLOB.selectedClass) {
      if (!typeOrDevice)
        result.item = cls;
      return result;
    }
    if (GLOB.selectedAllType && GLOB.selectedAllType !== RoutePath.GROUP && !allType && GLOB.selectedClass === clsPath && typ && itemName) {
      result.nav = { to: clsPath + GLOB.selectedAllType + groupPath + typeOrDevicePath + itemSearch + itemHash, options: { replace: true } };
      return result;
    }
    if (!itemName) {
      if (typ.children?.length &&
        (GLOB.selectedClass !== clsPath || GLOB.selectedAllType !== allType || GLOB.selectedType !== typeOrDevicePath || isDevicesTree(allType))) {
        const child = MenuNode.selectFirst(typ.children);
        if (child) {
          result.nav = { to: [clsPath, allType, typeOrDevicePath, MenuNode.getNodePath(child)].join(''), options: { replace: true } };
        }
      }

      commitSelection(clsPath, allType, group, typeOrDevicePath);
      GLOB.selectedItem = null;

      result.tree = root;
      return result;
    }

    // loaded menu
    if (typ?.children) {
      let item = typ.children.find((mn) => mn.path === itemPath);
      if (!item) {
        item = MenuNode.findDeepNode(typ.children, itemSearch, hwTypePath, group);
      }
      if (item) {
        if (GLOB.selectedClass !== clsPath || !allType && GLOB.selectedType !== typeOrDevicePath
          || allType && (!GLOB.selectedAllType || allType !== GLOB.selectedAllType)) {
          result.tree = root;
        }
        commitSelection(clsPath, allType, group, typeOrDevicePath);
        GLOB.selectedItem = item;
        if (!result.tree) result.tree = root;
        if (typeOrDevicePath === RoutePath.TOOLS) {
          result.tree = resolveToolsTree(result.tree, item);
        }

        if (item.key === RoutePath.TOPOLOGY) {
          const topology = findLastTopologyNode(item.children);
          if (topology) {
            result.nav = { to: [clsPath, allType, typeOrDevicePath, MenuNode.getNodePath(topology)].join(''), options: { replace: true } };
            return result;
          }
        }

        if (item.key === RoutePath.TRENDS) {
          const overview = item.children?.[0];
          if (overview) {
            result.nav = { to: [clsPath, allType, typeOrDevicePath, MenuNode.getNodePath(overview)].join(''), options: { replace: true } };
            return result;
          }
        }

        if (item.component || item.url)
          result.item = item;
        return result;
      }
    } else {
      //typ.children = []; or modify if in leftTree
    }

    // load from server
    let item: MenuNode;
    let tree = clsPath === GLOB.selectedClass && GLOB.selectedAllType === allType ? useAppGlobStore.getState().leftMenu : cls;

    if (tree && GLOB.selectedClass === clsPath && (!allType && GLOB.selectedType === typeOrDevicePath || allType && GLOB.selectedAllType)) {
      item = MenuNode.findDeepNode(tree.children, itemSearch, hwTypePath, group);
    } else {
      if (!root) return result;
      tree = root;
    }
    let reload = false;
    if (!item) {
      const urlGetPath = getPathUrl(clsPath, allType, group, typeOrDevicePath, itemSearch);
      await getApi<MenuResponseDTO | DefaultDashboardDTO>(urlGetPath, abortSignal).then(
        (response) => {
          if (abortSignal.aborted) return;
          let itemToSearch = itemSearch;
          let data = response.data.data;
          if (!data) {
            //Log.error('No page for '+itemSearch);
            return;
          }
          if (!Array.isArray(data)) {
            itemToSearch = '/' + data.dashboard.dashboard_id;
            data = data.path;
          }
          if (!tree.children) tree.children = [];
          let rootTree = tree;
          if (allType && clsName !== ClassKey.SAN && clsName !== ClassKey.LAN && clsName !== ClassKey.TAPE) {
            if (allType === RoutePath.TYPE) {
              rootTree = tree.children.find(mn => mn.path === typeOrDevicePath);
              if (!rootTree) return;
            } else if (allType === RoutePath.GROUP) {
              //rootTree = groupNode;
            } else {
              //rootTree = tree.children.find(mn => mn.title === typeOrDevice);
            }
            if (!rootTree.children) rootTree.children = [];
          }

          if (mergeTree(rootTree.children, data, group, clsPath)) {
            MenuNode.mapParent(rootTree.children, rootTree);
            tree.children = [...tree.children];
          }
          item = MenuNode.findDeepNode(tree.children, itemToSearch, hwTypePath, group);
          if (!item) {
            //chceks for if item not under group and url not contains group_id
            item = MenuNode.findDeepNode(tree.children, itemToSearch, hwTypePath, group, true);
          }
          tree = { ...tree };
          if (!item) {
            Log.error('Cannot find item in merged tree!', response);
          }
          else {
            const deviceNode = MenuNode.getHwTypeParent(item);
            const devicePath = deviceNode ? '/' + deviceNode.titleText : '';
            if (isDevicesTree(allType || GLOB.selectedAllType)
              && (typeOrDevicePath && devicePath != typeOrDevicePath || allType && GLOB.selectedAllType && allType !== GLOB.selectedAllType)
              && (allType !== RoutePath.GROUP && !rootTree.children.some(mn => mn.title === typeOrDevice)
                || allType === RoutePath.GROUP && !MenuNode.findDeepNode(rootTree.children, group))) {
              reload = true;
            }
          }
        },
        (reason: AxiosError<BasicResponseDTO>) => {
          if (abortSignal.aborted) return;
          const fallback = reason instanceof AxiosError
            ? findNavDomain(clsPath)?.loadErrorFallback?.(reason, urlGetPath)
            : null;
          if (fallback) {
            this.aborter.abort();
            setTimeout(() => {
              this.props.navigate(fallback);
            });
          } else Log.error('Failed to get menu path!', reason);
        }
      );
      if (abortSignal.aborted) return;
    } else {
      if (clsPath !== GLOB.selectedClass) {
        result.tree = tree;
      }
      commitSelection(clsPath, allType, group, typeOrDevicePath);
    }

    if (!item) {
      Log.warn('Navigation failed, no item ' + itemSearch + ' in tree!');
    } else {
      if (reload) {
        const deviceNode = MenuNode.getHwTypeParent(item);
        const devicePath = deviceNode ? '/' + deviceNode.titleText : '';
        const parentNode = MenuNode.getTopParent(item);
        if (devicePath !== typeOrDevicePath || parentNode !== deviceNode && deviceNode && allType !== RoutePath.GROUP) {
          const groupId = GLOB.selectedGroupId || group;
          result.nav = {
            to:
              [clsPath || GLOB.selectedClass, allType || GLOB.selectedAllType, groupId ? '/' + groupId : null, devicePath, itemSearch + itemHash]
                .filter(s => s).join(''), options: { replace: true }
          };
          result.tree = tree;
          return result;
        }
      }
      if (clsPath !== GLOB.selectedClass || clsPath === RoutePath.STORAGE && allType !== GLOB.selectedAllType) {
        result.tree = tree;
      }
      commitSelection(clsPath, allType, group, typeOrDevicePath);
      if (item !== GLOB.selectedItem) {
        result.tree = tree;
        GLOB.selectedItem = item;
      }

      result.item = item;
      return result;
    }
  }

  render(): ReactNode {
    return <>
      {this.state.loading ? <XmSpinner />
        : this.state.navItem && (this.state.navItem?.component ? this.state.navItem.component
          : (this.state.navItem.isLeaf && <BasePage url={this.state.navItem.url} />))}
    </>;
  }
}

export default withRouter(NavigateRoute);
